import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { apiClient } from '../utils/apiClient';
import { buildChatbotEmbed, buildChatbotSettingsPayload, createChatbotDraft, readChatbotSettingsResponse } from '../utils/chatbotSettings';
import './management-forms.css';

const inputStyle = { width: '100%', minWidth: 0, padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '4px', font: 'inherit', boxSizing: 'border-box' };
const sectionStyle = { borderTop: '1px solid #dee2e6', marginTop: '1.5rem', paddingTop: '1rem' };

const WebsiteChatbotSettings = () => {
  const { currentUser } = useApp();
  const canEdit = ['admin', 'superuser'].includes(currentUser?.user?.orgRole);
  const [draft, setDraft] = useState(createChatbotDraft);
  const [saved, setSaved] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loadVersion, setLoadVersion] = useState(0);
  const snippetRef = useRef(null);
  const embed = buildChatbotEmbed(saved?.widgetKey);
  const savedDraft = createChatbotDraft(saved);
  const hasUnsavedChanges = draft.website.trim() !== savedDraft.website.trim()
    || draft.instructions.trim() !== savedDraft.instructions.trim()
    || draft.welcomeMessage.trim() !== savedDraft.welcomeMessage.trim()
    || draft.enabled !== savedDraft.enabled || draft.bookingEnabled !== savedDraft.bookingEnabled;
  useEffect(() => {
    let active = true;
    setLoaded(false);
    setSaved(null);
    setError('');
    setMessage('');
    if (!canEdit || !currentUser?.sessionId) return;
    setLoading(true);
    apiClient('/chatbot/settings', { headers: { 'session-id': currentUser.sessionId } })
      .then(async response => {
        const data = await readChatbotSettingsResponse(response);
        if (data !== null && (!data || typeof data !== 'object' || Array.isArray(data))) throw new Error('Invalid chatbot settings response.');
        if (!active) return;
        setDraft(createChatbotDraft(data));
        setSaved(data);
        setLoaded(true);
      }).catch(failure => { if (active) setError(failure.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [canEdit, currentUser?.sessionId, currentUser?.user?.orgId, loadVersion]);
  const persist = async (payload, refreshed = false) => {
    setError('');
    setMessage('');
    setSaving(true);
    try {
      const response = await apiClient('/chatbot/settings', { method: 'POST', headers: { 'session-id': currentUser.sessionId, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await readChatbotSettingsResponse(response);
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid saved chatbot settings response.');
      const settings = { ...saved, ...data };
      const publicInformation = { ...saved?.publicInformation, ...data?.publicInformation };
      if (payload.publicInformation && data?.publicInformation?.welcomeMessage === undefined) {
        publicInformation.welcomeMessage = payload.publicInformation.welcomeMessage;
      }
      if (Object.keys(publicInformation).length) settings.publicInformation = publicInformation;
      for (const field of ['website', 'instructions', 'enabled', 'bookingEnabled']) {
        if (data[field] === undefined && payload[field] !== undefined) settings[field] = payload[field];
      }
      setSaved(settings);
      setDraft(createChatbotDraft(settings));
      setMessage(settings.widgetKey ? (refreshed ? 'Website information refreshed.' : 'Website chatbot settings saved.') : 'Settings saved, but no widget key was returned. Reload settings before generating an embed.');
    } catch (failure) { setError(failure.message); }
    finally { setSaving(false); }
  };
  const save = async event => {
    event.preventDefault();
    let payload;
    try { payload = buildChatbotSettingsPayload(draft, saved); }
    catch (failure) { setError(failure.message); return; }
    await persist(payload);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(embed);
      setMessage('Embed code copied.');
    } catch {
      snippetRef.current?.focus();
      snippetRef.current?.select();
      setError('Clipboard access is unavailable. The embed code is selected for copying.');
    }
  };
  if (!canEdit) return null;
  return (
    <section className="management-form">
      <h2>Website Chatbot</h2>
      {loading && <p role="status">Loading chatbot settings...</p>}
      {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
      {message && <p role="status" style={{ color: '#166534' }}>{message}</p>}
      {!loading && !loaded && <button type="button" onClick={() => setLoadVersion(previous => previous + 1)}>Retry loading settings</button>}
      {loaded && <form onSubmit={save}>
        <fieldset disabled={saving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <label style={{ display: 'block', maxWidth: '680px' }}>Website<input required type="url" value={draft.website} placeholder="https://your-flight-school.com" onChange={event => setDraft({ ...draft, website: event.target.value })} style={{ ...inputStyle, marginTop: '0.4rem' }} /></label>
          <div style={{ maxWidth: '680px', marginTop: '1rem' }}>
            <label htmlFor="chatbot-welcome-message">Welcome message (optional)</label>
            <textarea id="chatbot-welcome-message" rows={3} maxLength={1000} value={draft.welcomeMessage} onChange={event => setDraft({ ...draft, welcomeMessage: event.target.value })} aria-describedby="chatbot-welcome-guidance" placeholder="Welcome to {schoolName} at {locationName}. How can we help you fly?" style={{ ...inputStyle, marginTop: '0.4rem', resize: 'vertical', lineHeight: 1.5 }} />
            <div id="chatbot-welcome-guidance" style={{ color: '#526671', fontSize: '0.9rem', lineHeight: 1.5 }}>
              <p style={{ margin: '0.4rem 0' }}>Leave blank to use the generated greeting. Use {'{schoolName}'} and {'{locationName}'} as placeholders. Up to 1,000 characters.</p>
            </div>
          </div>
          <div style={{ maxWidth: '680px', marginTop: '1rem' }}>
            <label htmlFor="chatbot-instructions">Instructions</label>
            <textarea id="chatbot-instructions" required rows={6} value={draft.instructions} onChange={event => setDraft({ ...draft, instructions: event.target.value })} aria-describedby="chatbot-instructions-guidance" placeholder="Offer discovery flights and explain our training programs. Use a friendly, professional tone and refer unanswered questions to our office." style={{ ...inputStyle, marginTop: '0.4rem', resize: 'vertical', lineHeight: 1.5 }} />
            <div id="chatbot-instructions-guidance" style={{ color: '#526671', fontSize: '0.9rem', lineHeight: 1.5 }}>
              <p style={{ margin: '0.4rem 0' }}>Describe which services and offers to promote, how to explain the training experience, the preferred tone, and when to refer visitors to staff. Include school-approved booking, payment and cancellation guidance and any exclusions. Do not include secrets or customer information.</p>
              <p style={{ margin: '0.4rem 0' }}>These instructions guide chatbot behavior and offerings, not the school description. They cannot override verified prices, location scope, or booking safeguards.</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '1rem' }}>
            <label><input type="checkbox" checked={draft.enabled} onChange={event => setDraft({ ...draft, enabled: event.target.checked })} /> Enabled</label>
            <label><input type="checkbox" checked={draft.bookingEnabled} onChange={event => setDraft({ ...draft, bookingEnabled: event.target.checked })} /> Allow bookings</label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button type="submit" className="form-primary"><span aria-hidden="true">💾</span>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" disabled={!saved?.website || hasUnsavedChanges} onClick={() => persist({ refreshWebsite: true }, true)} title={hasUnsavedChanges ? 'Save changes before refreshing the saved website' : 'Refresh imported website information'}>Refresh website</button>
          </div>
        </fieldset>
      </form>}
      {saved?.widgetKey && <div style={sectionStyle}>
        <h3>Website Embed</h3>
        <textarea ref={snippetRef} aria-label="Website embed code" readOnly rows={7} value={embed} style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }} />
        <button type="button" onClick={copy} style={{ marginTop: '0.5rem' }}>Copy embed code</button>
      </div>}
    </section>
  );
};

export default WebsiteChatbotSettings;